/**
 * The live notification feed, and the push subscription behind it.
 *
 * Two ways of being told, for two different situations: the SSE stream while
 * the tab is open, and Web Push when it is not. They carry the same shape, so
 * the UI renders one component either way.
 */

import { RESYNC_GAP_MS, shouldResync } from '@/net/badge';
import {
  apiBase,
  markNotificationsRead,
  notifications,
  subscribePush,
  unsubscribePush,
  vapidKey,
  type AppNotification,
} from '@/net/api';

/* ------------------------------------------------------------- the stream -- */

export interface FeedCallbacks {
  /** A notification arrived while the tab was open. */
  onNotification(notification: AppNotification): void;
  /** The unread count changed, from any cause. */
  onUnread(count: number): void;
  /**
   * The whole inbox, re-read from the server: a foreground resync. A row read on another device,
   * or a push the stream missed, is corrected here, so the dot, the icon badge and the tray follow.
   */
  onResync?(items: AppNotification[]): void;
}

/** While the app is open and visible the inbox is re-read this often (a read on another device has no push). */
const VISIBLE_RESYNC_MS = 60_000;

/**
 * Hold a live feed open, and keep it open.
 *
 * `EventSource` reconnects by itself — that is most of why this is SSE and not
 * a websocket — but it only does so while the browser thinks the page is
 * alive. A phone that sleeps kills the connection without the reconnect ever
 * firing, so returning to the tab re-syncs from the REST list rather than
 * trusting the stream to have missed nothing.
 */
export class Feed {
  readonly #callbacks: FeedCallbacks;
  #source: EventSource | null = null;
  #unread = 0;
  /** The newest createdAt seen, so a re-sync asks only for what it missed. */
  #watermark: string | null = null;
  #stopped = false;

  constructor(callbacks: FeedCallbacks) {
    this.#callbacks = callbacks;
  }

  get unread(): number {
    return this.#unread;
  }

  /** Load what is already there, then open the stream. */
  async start(): Promise<AppNotification[]> {
    this.#stopped = false;
    const initial = await this.#sync();
    this.#open();
    // Re-sync when the tab comes back: a sleeping phone drops the connection
    // without EventSource noticing, and the events sent meanwhile are gone.
    document.addEventListener('visibilitychange', this.#onVisible);
    window.addEventListener('focus', this.#onVisible);
    window.addEventListener('pageshow', this.#onVisible);
    window.addEventListener('online', this.#onVisible);
    // A push the worker saw while the app was open: the stream may be slow or down, ask the server now.
    navigator.serviceWorker?.addEventListener('message', this.#onWorker);
    this.#timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void this.resync();
    }, VISIBLE_RESYNC_MS);
    this.#lastResync = Date.now();
    return initial;
  }

  stop(): void {
    this.#stopped = true;
    document.removeEventListener('visibilitychange', this.#onVisible);
    window.removeEventListener('focus', this.#onVisible);
    window.removeEventListener('pageshow', this.#onVisible);
    window.removeEventListener('online', this.#onVisible);
    navigator.serviceWorker?.removeEventListener('message', this.#onWorker);
    window.clearInterval(this.#timer);
    this.#source?.close();
    this.#source = null;
  }

  #timer = 0;
  #lastResync = 0;

  /** Re-read the whole inbox. A burst (focus + visibility + pageshow) is one read; `force` skips that debounce. */
  async resync(force = false): Promise<void> {
    const now = Date.now();
    if (this.#stopped || (!force && !shouldResync(this.#lastResync, now, RESYNC_GAP_MS))) return;
    this.#lastResync = now;
    const response = await notifications(null);
    if (!response.ok || this.#stopped) return;
    const items = response.value.items;
    const newest = items[0]?.createdAt;
    if (newest) this.#watermark = newest;
    this.#setUnread(response.value.unread);
    this.#callbacks.onResync?.(items);
  }

  /** Mark read locally and on the server. Omit `ids` for "all". */
  async markRead(ids?: readonly string[]): Promise<void> {
    const response = await markNotificationsRead(ids);
    if (response.ok) this.#setUnread(response.value.unread);
    // The server did not hear it: the optimistic read must not stand as the truth (the dot and the
    // home-screen badge would say "read" for rows the server still counts). Re-read.
    else void this.resync(true);
  }

  readonly #onVisible = (): void => {
    if (document.visibilityState === 'visible' && !this.#stopped) void this.resync();
  };

  readonly #onWorker = (event: MessageEvent): void => {
    if ((event.data as { type?: string } | null)?.type === 'push-received') void this.resync(true);
  };

  async #sync(): Promise<AppNotification[]> {
    const response = await notifications(this.#watermark);
    if (!response.ok) return [];
    this.#setUnread(response.value.unread);
    const items = response.value.items;
    const newest = items[0]?.createdAt;
    if (newest) this.#watermark = newest;
    return items;
  }

  #open(): void {
    if (this.#source) return;
    // `withCredentials` so the session cookie rides along — without it the
    // stream authenticates as nobody and closes immediately.
    const source = new EventSource(`${apiBase()}/api/notifications/stream`, {
      withCredentials: true,
    });
    source.addEventListener('notification', (event) => {
      try {
        const notification = JSON.parse((event as MessageEvent<string>).data) as AppNotification;
        this.#watermark = notification.createdAt;
        this.#setUnread(this.#unread + 1);
        this.#callbacks.onNotification(notification);
      } catch {
        // A frame we cannot parse is a frame we ignore; the next re-sync will
        // pick up whatever it described.
      }
    });
    this.#source = source;
  }

  #setUnread(count: number): void {
    if (count === this.#unread) return;
    this.#unread = count;
    this.#callbacks.onUnread(count);
  }
}

/* ----------------------------------------------------------------- push -- */

/**
 * What the push prompt is allowed to say right now.
 *
 * `needs-install` is iOS: Safari grants no push permission to a site opened in
 * the browser, only to one added to the Home Screen. Showing a "turn on
 * notifications" button there produces a permission prompt that never appears
 * and a player who thinks the feature is broken — so that case gets its own
 * instructions instead of a button.
 */
export type PushState = 'unsupported' | 'needs-install' | 'off' | 'on' | 'denied';

function standalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return isIos() && !standalone() ? 'needs-install' : 'unsupported';
  }
  if (isIos() && !standalone()) return 'needs-install';
  if (Notification.permission === 'denied') return 'denied';
  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();
  return existing ? 'on' : 'off';
}

/** Register the worker. Safe to call on every load; does nothing if push is off. */
export async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

/**
 * Ask for permission and subscribe. Returns the state the UI should now show.
 *
 * Must be called from a user gesture — browsers refuse a permission prompt
 * that nobody asked for, and a refusal here is indistinguishable from a denial
 * unless the click is the caller's.
 */
export async function enablePush(): Promise<PushState> {
  const key = await vapidKey();
  // No key means push is not configured on this deployment at all. Nothing to
  // subscribe to, and no permission worth spending on it.
  if (!key) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const registration = (await navigator.serviceWorker.getRegistration()) ?? (await registerWorker());
  if (!registration) return 'unsupported';
  await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });
  const saved = await subscribePush(subscription.toJSON());
  return saved.ok ? 'on' : 'off';
}

export async function disablePush(): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return 'off';
  // Tell the server first: a subscription forgotten locally but kept
  // server-side is one that keeps buzzing a phone nobody can turn off.
  await unsubscribePush(subscription.endpoint);
  await subscription.unsubscribe();
  return 'off';
}

/**
 * The VAPID key travels as base64url and `subscribe` wants raw bytes.
 *
 * `atob` cannot read base64url — it rejects `-` and `_` and requires the
 * padding that base64url drops — so both have to be undone by hand. Passing
 * the string through unconverted fails with an opaque InvalidCharacterError.
 */
function urlBase64ToUint8Array(base64: string): BufferSource {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  // Allocated from an explicit ArrayBuffer: `new Uint8Array(n)` is typed over
  // ArrayBufferLike, which includes SharedArrayBuffer, and `subscribe` will
  // not take one of those.
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
